<script lang="ts">
  import { ResponsiveVars } from '@twist/core';
  import { cx } from '@emotion/css';
  import type { Quote } from '$lib/sanity/schemas';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import PortableText from '$components/SanityComponents/PortableText/PortableText.svelte';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';

  interface Props {
    document: Quote;
    sourceMap: ContentSourceMap;
    path?: StudioPathLike;
  }

  let { document, sourceMap, path = [] }: Props = $props();

  let encodeDataAttribute = $derived(useEncodeDataAttribute(document, sourceMap, sanityStudioURL));

  let responsiveVars = $derived(
    new ResponsiveVars()
      .addFluid('margin-vertical', toFluidVarInput(32, 48))
      .addFluid('author-margin-top', toFluidVarInput(24, 36))
      .addFluid('author-padding-x', toFluidVarInput(26.67, 0))
      .toCSS()
  );
</script>

<figure class={cx('quote', responsiveVars)}>
  <blockquote data-sanity={encodeDataAttribute([...path, 'copy'])}>
    <PortableText {sourceMap} path={[...path, 'body']} body={document.copy} />
  </blockquote>
  {#if document.authorInfo?.name}
    <figcaption class="author">
      <div data-sanity={encodeDataAttribute([...path, 'authorInfo', 'name'])}>
        <PortableText {sourceMap} path={[...path, 'body']} body={document.authorInfo.name} />
      </div>
      {#if document.authorInfo?.title}
        <div
          class="author-title"
          data-sanity={encodeDataAttribute([...path, 'authorInfo', 'title'])}
        >
          <PortableText {sourceMap} path={[...path, 'body']} body={document.authorInfo.title} />
        </div>
      {/if}
    </figcaption>
  {/if}
</figure>

<style lang="scss">
  @import 'src/scss/media';

  .quote {
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: var(--margin-vertical) 0;

    .author {
      display: flex;
      flex-direction: column;
      margin-top: var(--author-margin-top);

      .author-title {
        padding: 0 var(--author-padding-x);
      }
    }
  }
</style>
